import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronUp, ChevronDown, Filter, X, Eye, EyeOff, Save, Trash2,
  Star, CalendarPlus, MessageSquare, Phone, MapPin,
} from "lucide-react";
import api from "../lib/api";
// Iter 228 — Log a call removed from leads grid; not in HV daily flow.
import UnseenDot from "./UnseenDot";

/**
 * Excel-grade leads table.
 *
 * Features
 *   • Click any column header to sort asc/desc/none (tri-state)
 *   • Shift-click adds a secondary sort; repeat for tertiary, etc.
 *   • Per-column filter popover:
 *       – text columns: contains / equals substring match
 *       – enum columns: multi-select checkboxes
 *       – numeric columns: min/max range
 *       – boolean columns: Yes/No/—
 *   • Active filter chips up top with one-click remove
 *   • Column visibility toggle (persists per-user)
 *   • Named saved presets ("Kruger trip 3★+", etc.) — stored on
 *     user.preferences.lead_table_presets
 *   • Row actions: Meeting, Call log, WhatsApp (like the kanban card)
 *   • Mobile: horizontally scrolls with a fixed first column
 */

const COLUMNS = [
  { key: "title",             label: "Lodge / Name",    type: "text",   width: 220, essential: true },
  { key: "customer_name",     label: "Customer",        type: "text",   width: 160 },
  { key: "province_area",     label: "Prov.",           type: "enum",   width: 80  },
  { key: "nearest_town",      label: "Area",            type: "text",   width: 130 },
  { key: "estate_reserve",    label: "Estate / Reserve",type: "text",   width: 180 },
  { key: "star_rating",       label: "★",               type: "number", width: 60  },
  { key: "lead_rating",       label: "Lead rating",     type: "number", width: 100 },
  { key: "price_per_night",   label: "Price / night",   type: "number", width: 110 },
  { key: "value",             label: "Deal value",      type: "number", width: 120 },
  { key: "has_wtp",           label: "WTP",             type: "bool",   width: 70  },
  { key: "has_sla",           label: "SLA",             type: "bool",   width: 70  },
  { key: "needs_system",      label: "Needs system",    type: "bool",   width: 100 },
  { key: "phase",             label: "Phase",           type: "enum",   width: 140 },
  { key: "own_contact_name",  label: "Contact",         type: "text",   width: 140 },
  { key: "own_contact_phone", label: "Phone",           type: "text",   width: 130 },
  { key: "last_contact_date", label: "Last contact",    type: "date",   width: 110 },
  { key: "next_followup_date",label: "Next follow-up",  type: "date",   width: 120 },
  { key: "current_provider",  label: "Current provider",type: "text",   width: 140 },
  { key: "lead_source",       label: "Source",          type: "text",   width: 120 },
];

const DEFAULT_VISIBLE = ["title", "customer_name", "province_area", "nearest_town", "star_rating", "lead_rating", "price_per_night", "phase", "own_contact_phone", "last_contact_date"];

function val(lead, key, customers) {
  if (key === "customer_name") {
    const c = customers.find((x) => x.id === lead.customer_id);
    return c?.name || lead.customer_name || "";
  }
  return lead[key];
}

function cmp(a, b) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b));
}

export default function LeadsTableView({ leads, customers, phaseGroups, onMeeting, isUnseen, dismissUnseen }) {
  const navigate = useNavigate();
  // Visible columns (persisted to localStorage)
  const [visible, setVisible] = useState(() => {
    try {
      const saved = localStorage.getItem("hv_lead_table_cols");
      if (saved) return new Set(JSON.parse(saved));
    } catch (_e) { /* noop */ }
    return new Set(DEFAULT_VISIBLE);
  });
  const persistVisible = (next) => {
    setVisible(next);
    try { localStorage.setItem("hv_lead_table_cols", JSON.stringify([...next])); } catch (_e) { /* noop */ }
  };
  const toggleCol = (k) => {
    const n = new Set(visible);
    if (n.has(k)) n.delete(k); else n.add(k);
    persistVisible(n);
  };

  // Row selection (for bulk actions e.g. "Plan a trip")
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const [planningTrip, setPlanningTrip] = useState(false);
  const planTripWithSelected = async () => {
    if (selectedIds.size === 0) return;
    setPlanningTrip(true);
    try {
      const picks = leads.filter((l) => selectedIds.has(l.id));
      const today = new Date().toISOString().slice(0, 10);
      const stops = picks.map((l) => ({
        lead_id: l.id,
        customer_id: l.customer_id || null,
        title: l.title || l.customer_name || "(lead)",
        address: [l.estate_reserve, l.nearest_town, l.province_area].filter(Boolean).join(", "),
      }));
      const { data } = await api.post("/trips", {
        title: `Trip · ${picks.length} stop${picks.length === 1 ? "" : "s"} · ${today}`,
        start_date: today,
        stops,
      });
      navigate(`/trips/${data.id}`);
    } catch (_e) { /* noop */ }
    setPlanningTrip(false);
  };

  // Multi-sort: [{ key, dir: "asc"|"desc" }, ...]
  const [sorts, setSorts] = useState([]);
  const cycleSort = (key, shift) => {
    setSorts((prev) => {
      const existing = prev.find((s) => s.key === key);
      const others = shift ? prev.filter((s) => s.key !== key) : [];
      if (!existing) return shift ? [...others, { key, dir: "asc" }] : [{ key, dir: "asc" }];
      if (existing.dir === "asc") return shift ? [...others, { key, dir: "desc" }] : [{ key, dir: "desc" }];
      // asc → desc → none
      return shift ? others : [];
    });
  };

  // Per-column filters
  // filters shape: { [key]: { op, value } } — text: {contains}; enum: {in: []};
  //   number: {min, max}; bool: {value: true|false|null}
  const [filters, setFilters] = useState({});
  const setFilter = (key, patch) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (patch === null) delete next[key];
      else next[key] = { ...prev[key], ...patch };
      return next;
    });
  };
  const clearFilter = (key) => setFilter(key, null);
  const clearAll = () => setFilters({});

  // Active filter popover
  const [openFilter, setOpenFilter] = useState(null);

  // Enum values derived from the data
  const enumValues = useMemo(() => {
    const out = {};
    COLUMNS.filter((c) => c.type === "enum").forEach((c) => {
      out[c.key] = [...new Set(leads.map((l) => l[c.key]).filter(Boolean))].sort();
    });
    return out;
  }, [leads]);

  const phaseLabel = (k) => {
    for (const g of phaseGroups || []) {
      for (const p of g.phases || []) if (p.key === k) return p.label;
    }
    return k;
  };

  // Apply filters + sort
  const rows = useMemo(() => {
    let out = leads.slice();
    Object.entries(filters).forEach(([key, f]) => {
      const col = COLUMNS.find((c) => c.key === key);
      if (!col) return;
      if (col.type === "text" || col.type === "date") {
        if (f.contains) {
          const q = String(f.contains).toLowerCase();
          out = out.filter((l) => String(val(l, key, customers) || "").toLowerCase().includes(q));
        }
      } else if (col.type === "number") {
        if (f.min != null && f.min !== "") out = out.filter((l) => (Number(l[key]) || 0) >= Number(f.min));
        if (f.max != null && f.max !== "") out = out.filter((l) => (Number(l[key]) || 0) <= Number(f.max));
      } else if (col.type === "enum") {
        if (Array.isArray(f.in) && f.in.length) out = out.filter((l) => f.in.includes(l[key]));
      } else if (col.type === "bool") {
        if (f.value === true) out = out.filter((l) => l[key] === true);
        else if (f.value === false) out = out.filter((l) => l[key] === false);
      }
    });
    if (sorts.length) {
      out = out.slice().sort((a, b) => {
        for (const s of sorts) {
          const r = cmp(val(a, s.key, customers), val(b, s.key, customers));
          if (r !== 0) return s.dir === "asc" ? r : -r;
        }
        return 0;
      });
    }
    return out;
  }, [leads, filters, sorts, customers]);

  const visibleCols = COLUMNS.filter((c) => visible.has(c.key));
  const activeFilterKeys = Object.keys(filters).filter((k) => {
    const f = filters[k];
    if (!f) return false;
    if ("contains" in f && f.contains) return true;
    if ("in" in f && f.in?.length) return true;
    if (("min" in f && f.min !== "" && f.min != null) || ("max" in f && f.max !== "" && f.max != null)) return true;
    if ("value" in f && (f.value === true || f.value === false)) return true;
    return false;
  });

  // Presets
  const [presets, setPresets] = useState([]);
  useEffect(() => {
    api.get("/users/me").then(({ data }) => {
      setPresets(data?.preferences?.lead_table_presets || []);
    }).catch(() => {});
  }, []);
  const savePreset = async () => {
    const name = prompt("Name this preset (e.g. 'Kruger trip 3★+')");
    if (!name) return;
    const preset = { name, filters, sorts, cols: [...visible] };
    const next = [...presets.filter((p) => p.name !== name), preset];
    setPresets(next);
    await api.patch("/users/me/preferences", { lead_table_presets: next }).catch(() => {});
  };
  const applyPreset = (p) => {
    setFilters(p.filters || {});
    setSorts(p.sorts || []);
    if (p.cols) persistVisible(new Set(p.cols));
  };
  const deletePreset = async (name) => {
    const next = presets.filter((p) => p.name !== name);
    setPresets(next);
    await api.patch("/users/me/preferences", { lead_table_presets: next }).catch(() => {});
  };

  return (
    <div className="space-y-3" data-testid="leads-table-view">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-[#647087]">
          Showing <span className="font-mono text-white">{rows.length}</span> of {leads.length} leads
        </span>
        {activeFilterKeys.length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="lead-table-clear-filters"
          >
            <X className="w-3 h-3" /> Clear all filters
          </button>
        )}
        {selectedIds.size > 0 && (
          <>
            <span className="text-xs text-[#1fb6c1] font-mono" data-testid="lead-table-selected-count">
              {selectedIds.size} selected
            </span>
            <button
              type="button"
              onClick={planTripWithSelected}
              disabled={planningTrip}
              className="hv-btn hv-btn-primary text-xs"
              data-testid="lead-table-plan-trip"
              title="Create a new trip with the selected leads as stops"
            >
              <MapPin className="w-3 h-3" /> {planningTrip ? "Creating…" : `Plan a trip (${selectedIds.size})`}
            </button>
            <button
              type="button"
              onClick={clearSelection}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="lead-table-clear-selection"
            >
              <X className="w-3 h-3" /> Clear
            </button>
          </>
        )}
        <div className="ml-auto flex items-center gap-2">
          {presets.length > 0 && (
            <select
              onChange={(e) => {
                const p = presets.find((x) => x.name === e.target.value);
                if (p) applyPreset(p);
              }}
              className="hv-input text-xs py-1.5"
              data-testid="lead-table-preset-select"
              defaultValue=""
            >
              <option value="" disabled>Apply preset…</option>
              {presets.map((p) => (
                <option key={p.name} value={p.name}>{p.name}</option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={savePreset}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="lead-table-save-preset"
            title="Save current filters, sort and columns as a named preset"
          >
            <Save className="w-3 h-3" /> Save preset
          </button>
          <ColumnToggle visible={visible} onToggle={toggleCol} />
        </div>
      </div>

      {/* Active filter chips */}
      {activeFilterKeys.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {activeFilterKeys.map((k) => {
            const col = COLUMNS.find((c) => c.key === k);
            const f = filters[k];
            let text = "";
            if (f.contains) text = `"${f.contains}"`;
            else if (f.in?.length) text = f.in.slice(0, 2).join(", ") + (f.in.length > 2 ? `+${f.in.length - 2}` : "");
            else if (f.min != null || f.max != null) text = `${f.min ?? "—"} to ${f.max ?? "—"}`;
            else if (f.value === true) text = "Yes";
            else if (f.value === false) text = "No";
            return (
              <span key={k} className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border border-[#1fb6c1]/40 bg-[#1fb6c1]/10 text-[#1fb6c1] flex items-center gap-1.5" data-testid={`filter-chip-${k}`}>
                {col.label}: {text}
                <button type="button" onClick={() => clearFilter(k)} className="hover:text-white" data-testid={`filter-chip-${k}-clear`}>
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
          {presets.length > 0 && (
            <div className="flex flex-wrap gap-1.5 ml-2">
              {presets.map((p) => (
                <span key={p.name} className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border border-white/10 text-[#a0aabf]">
                  {p.name}
                  <button type="button" onClick={() => deletePreset(p.name)} className="ml-1 hover:text-rose-300" title={`Delete preset ${p.name}`}>×</button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto border border-white/10 rounded-md">
        <table className="w-full text-xs" data-testid="lead-table">
          <thead className="bg-white/5 sticky top-0 z-10">
            <tr>
              <th className="px-2 py-2 text-left border-b border-white/10" style={{ width: 36 }}>
                <input
                  type="checkbox"
                  checked={rows.length > 0 && rows.every((l) => selectedIds.has(l.id))}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedIds(new Set([...selectedIds, ...rows.map((r) => r.id)]));
                    } else {
                      const n = new Set(selectedIds);
                      rows.forEach((r) => n.delete(r.id));
                      setSelectedIds(n);
                    }
                  }}
                  className="accent-[#1fb6c1]"
                  data-testid="lead-table-select-all"
                  title="Select/deselect all visible rows"
                />
              </th>
              {visibleCols.map((c) => {
                const sort = sorts.find((s) => s.key === c.key);
                const sortIdx = sorts.findIndex((s) => s.key === c.key);
                const isFiltered = !!filters[c.key] && activeFilterKeys.includes(c.key);
                return (
                  <th
                    key={c.key}
                    className="px-2 py-2 text-left font-bold uppercase tracking-wider text-[10px] border-b border-white/10 select-none"
                    style={{ minWidth: c.width, width: c.width }}
                  >
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={(e) => cycleSort(c.key, e.shiftKey)}
                        className={`flex-1 text-left flex items-center gap-1 hover:text-[#1fb6c1] ${sort ? "text-[#1fb6c1]" : ""}`}
                        title="Click to sort. Shift-click to add secondary sort."
                        data-testid={`col-sort-${c.key}`}
                      >
                        {c.label}
                        {sort && (sort.dir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                        {sorts.length > 1 && sortIdx >= 0 && (
                          <span className="text-[9px] text-[#647087]">{sortIdx + 1}</span>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => setOpenFilter(openFilter === c.key ? null : c.key)}
                        className={`p-0.5 rounded hover:text-[#1fb6c1] relative ${isFiltered ? "text-[#1fb6c1]" : "text-[#647087]"}`}
                        title="Filter"
                        data-testid={`col-filter-${c.key}`}
                      >
                        <Filter className="w-3 h-3" />
                        {isFiltered && (
                          <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-[#1fb6c1]"></span>
                        )}
                      </button>
                    </div>
                    {openFilter === c.key && (
                      <FilterPopover
                        column={c}
                        value={filters[c.key] || {}}
                        enumValues={enumValues[c.key]}
                        phaseLabel={phaseLabel}
                        onChange={(patch) => setFilter(c.key, patch)}
                        onClose={() => setOpenFilter(null)}
                      />
                    )}
                  </th>
                );
              })}
              <th className="px-2 py-2 text-right text-[10px] font-bold uppercase tracking-wider border-b border-white/10" style={{ width: 140 }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={visibleCols.length + 2} className="px-3 py-8 text-center text-xs text-[#647087]">
                  No leads match the current filters.
                </td>
              </tr>
            ) : rows.map((l) => (
              <tr
                key={l.id}
                className={`border-b border-white/5 hover:bg-[#1fb6c1]/5 transition cursor-pointer ${selectedIds.has(l.id) ? "bg-[#1fb6c1]/10" : ""} ${isUnseen?.(l) ? "hv-unseen" : ""}`}
                onClick={() => { dismissUnseen?.(l.id); navigate(`/leads/${l.id}`); }}
                data-testid={`lead-table-row-${l.id}`}
                data-focus-id={l.id}
              >
                <td className="px-2 py-2 align-top" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-1.5">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(l.id)}
                      onChange={() => toggleRow(l.id)}
                      className="accent-[#1fb6c1]"
                      data-testid={`lead-table-row-check-${l.id}`}
                    />
                    <UnseenDot unseen={isUnseen?.(l)} testId={`lead-unseen-${l.id}`} />
                  </div>
                </td>
                {visibleCols.map((c) => (
                  <td key={c.key} className="px-2 py-2 align-top">
                    <Cell lead={l} col={c} customers={customers} phaseLabel={phaseLabel} />
                  </td>
                ))}
                <td className="px-2 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1">
                    {onMeeting && (
                      <button
                        type="button"
                        onClick={() => onMeeting(l)}
                        className="hv-btn hv-btn-ghost text-[10px] px-1.5 py-1"
                        title="Schedule meeting"
                        data-testid={`lead-table-meeting-${l.id}`}
                      >
                        <CalendarPlus className="w-3 h-3 text-[#A78BFA]" />
                      </button>
                    )}
                    {/* Iter 228 — Log a call removed from leads grid; not in HV daily flow. */}
                    {l.own_contact_phone && (
                      <a
                        href={`https://wa.me/${(l.own_contact_phone || "").replace(/\D/g, "")}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="hv-btn hv-btn-ghost text-[10px] px-1.5 py-1"
                        title="WhatsApp"
                        data-testid={`lead-table-whatsapp-${l.id}`}
                      >
                        <MessageSquare className="w-3 h-3 text-[#22C55E]" />
                      </a>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Cell({ lead, col, customers, phaseLabel }) {
  const v = val(lead, col.key, customers);
  if (v == null || v === "") return <span className="text-[#647087]">—</span>;
  if (col.type === "bool") {
    if (v === true) return <span className="text-emerald-300 font-mono">✓</span>;
    if (v === false) return <span className="text-rose-300 font-mono">✗</span>;
    return <span className="text-[#647087]">—</span>;
  }
  if (col.type === "date") {
    try { return <span className="text-[#a0aabf] font-mono text-[11px]">{new Date(v).toLocaleDateString("en-GB")}</span>; } catch (_e) { return v; }
  }
  if (col.key === "star_rating" || col.key === "lead_rating") {
    const n = Number(v);
    return (
      <span className="inline-flex items-center gap-0.5" data-testid={`cell-${col.key}-${n}`}>
        {[1,2,3,4,5].map((i) => (
          <Star key={i} className={`w-3 h-3 ${i <= n ? "text-amber-300 fill-amber-300" : "text-white/10"}`} />
        ))}
      </span>
    );
  }
  if (col.key === "value" || col.key === "price_per_night") {
    return <span className="text-[#1fb6c1] font-mono">{Number(v) > 0 ? `R ${Number(v).toLocaleString("en-GB")}` : "—"}</span>;
  }
  if (col.key === "phase") {
    return <span className="text-[11px] uppercase tracking-wider text-[#a0aabf]">{phaseLabel(v)}</span>;
  }
  return <span className="truncate block">{String(v)}</span>;
}

function ColumnToggle({ visible, onToggle }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="hv-btn hv-btn-ghost text-xs"
        data-testid="lead-table-cols-btn"
      >
        <Eye className="w-3 h-3" /> Columns
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-60 max-h-[60vh] overflow-y-auto hv-card p-2 z-50 shadow-2xl" onMouseLeave={() => setOpen(false)}>
          {COLUMNS.map((c) => (
            <label
              key={c.key}
              className="flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-white/5 rounded cursor-pointer"
              data-testid={`col-toggle-${c.key}`}
            >
              <input type="checkbox" checked={visible.has(c.key)} onChange={() => onToggle(c.key)} className="accent-[#1fb6c1]" />
              <span className="flex-1">{c.label}</span>
              {visible.has(c.key) ? <Eye className="w-3 h-3 text-[#1fb6c1]" /> : <EyeOff className="w-3 h-3 text-[#647087]" />}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function FilterPopover({ column, value, enumValues, phaseLabel, onChange, onClose }) {
  const c = column;
  return (
    <div
      className="absolute mt-1 hv-card p-3 z-50 shadow-2xl w-64 text-xs normal-case tracking-normal font-normal"
      onClick={(e) => e.stopPropagation()}
      data-testid={`filter-popover-${c.key}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#1fb6c1] font-bold">Filter · {c.label}</span>
        <button type="button" onClick={onClose} className="text-[#647087] hover:text-white"><X className="w-3 h-3" /></button>
      </div>
      {(c.type === "text" || c.type === "date") && (
        <input
          autoFocus
          placeholder="Contains…"
          value={value.contains || ""}
          onChange={(e) => onChange({ contains: e.target.value })}
          className="hv-input text-xs py-1.5 w-full"
          data-testid={`filter-text-${c.key}`}
        />
      )}
      {c.type === "number" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          <input type="number" placeholder="min" value={value.min ?? ""} onChange={(e) => onChange({ min: e.target.value })} className="hv-input text-xs py-1.5" data-testid={`filter-min-${c.key}`} />
          <input type="number" placeholder="max" value={value.max ?? ""} onChange={(e) => onChange({ max: e.target.value })} className="hv-input text-xs py-1.5" data-testid={`filter-max-${c.key}`} />
        </div>
      )}
      {c.type === "enum" && (
        <div className="space-y-1 max-h-56 overflow-y-auto">
          {(enumValues || []).map((v) => (
            <label key={v} className="flex items-center gap-2 px-1 py-1 hover:bg-white/5 rounded cursor-pointer">
              <input
                type="checkbox"
                checked={(value.in || []).includes(v)}
                onChange={(e) => {
                  const set = new Set(value.in || []);
                  if (e.target.checked) set.add(v); else set.delete(v);
                  onChange({ in: [...set] });
                }}
                className="accent-[#1fb6c1]"
                data-testid={`filter-enum-${c.key}-${v}`}
              />
              <span className="truncate">{c.key === "phase" ? phaseLabel(v) : v}</span>
            </label>
          ))}
          {(!enumValues || enumValues.length === 0) && <div className="text-[#647087] italic px-1">No values in current data.</div>}
        </div>
      )}
      {c.type === "bool" && (
        <div className="flex gap-1.5">
          <button type="button" onClick={() => onChange({ value: value.value === true ? null : true })} className={`flex-1 hv-btn text-xs ${value.value === true ? "hv-btn-primary" : "hv-btn-ghost"}`}>Yes</button>
          <button type="button" onClick={() => onChange({ value: value.value === false ? null : false })} className={`flex-1 hv-btn text-xs ${value.value === false ? "hv-btn-primary" : "hv-btn-ghost"}`}>No</button>
        </div>
      )}
      <button
        type="button"
        onClick={() => { onChange({ contains: "", in: [], min: "", max: "", value: null }); onClose(); }}
        className="w-full mt-2 text-[10px] text-[#647087] hover:text-white py-1"
        data-testid={`filter-clear-${c.key}`}
      >
        Clear this filter
      </button>
    </div>
  );
}
